import { useState } from 'react';
import type { UserPermissionVm } from '@repo/services';
import { useGetUserSecurityQuery, useGraphService } from '@repo/services';
import useAsyncEffect from './useAsyncEffect';

/**
 * Loads the current Graph user profile alongside PrismCore security data (apps, roles, permissions) via `useGetUserSecurityQuery`, exposing both the raw `UserPermissionVm[]` and a flattened permission-key array for use with the `can*()` permission helpers, as well as user simulation status.
 *
 * @returns `{ currentUser, currentUserPhoto, userApps, userRoles, userPermissions, userPermissionKeys, isSimulating, isSimulatingRole, simulatedRoleLabel, realUserEmail, realUserFullName, isUnrestricted, allowedPropertyCount, mapStateProvinceFilter, canViewFinancials, securityLoading, userLoading, isLoading }`.
 */
export const useCurrentUser = () => {
    const { getCurrentUser } = useGraphService();
    const [currentUser, setCurrentUser] = useState<any>(undefined);
    /** Object-URL string for the current user's Graph profile photo, or undefined until loaded/if unavailable. */
    const [currentUserPhoto, setCurrentUserPhoto] = useState<string | undefined>(undefined);
    const [userLoading, setUserLoading] = useState(true);

    const { currentData: security, isLoading: securityLoading } = useGetUserSecurityQuery(null);

    const userApps = security?.apps ?? [];
    const userRoles = security?.roles?.map(r => r.fullRoleKey) ?? [];

    /** Rich permission objects — { permissionKey, appKey }. Use when app-scoping matters. */
    const userPermissions: UserPermissionVm[] = security?.permissions ?? [];

    /** Flat set of permission keys for quick membership checks. Backward-compatible with can*() helpers. */
    const userPermissionKeys: string[] = userPermissions.map(p => p.permissionKey);

    const isSimulating = security?.isSimulating ?? false;
    const realUserEmail = security?.realUserEmail;
    const realUserFullName = security?.realUserFullName;

    const isUnrestricted = security?.isUnrestricted ?? true;
    const allowedPropertyCount = security?.allowedPropertyCount ?? null;
    const dataScopes = security?.dataScopes ?? [];

    /**
     * State/province codes to filter the Properties portfolio map by (SPEC-AUTH-003 §17 app-scoping
     * decision — client-side interim only). `null` means "show everything", which is also the
     * correct default while security is still loading: unlike `canViewFinancials`, this is NOT a
     * security boundary, so there is no reason to hide the map pending a network round trip.
     */
    const mapStateProvinceFilter = security?.mapStateProvinceFilter ?? null;

    /**
     * Field-level redaction access for financial fields (SPEC-AUTH-003 §7.4, §17 Q7).
     * Fails closed (`false`) while security data is loading to prevent UI flashes of sensitive columns.
     */
    const canViewFinancials = securityLoading
        ? false
        : (security?.protectedFieldGroups?.find(g => g.groupKey === 'Financials')?.canView ?? true);

    // Heuristic: when simulating a role (rather than a user), the backend encodes the
    // synthetic identity as `email: "role:<AppKey>.<RoleName>"` and `fullName: "Role: <DisplayName>"`.
    const isSimulatingRole = Boolean(isSimulating && security?.email?.startsWith('role:'));
    const simulatedRoleLabel = isSimulatingRole
        ? security?.fullName?.replace(/^Role:\s*/, '')
        : undefined;

    const getUserAction = async () => {
        try {
            const { user, photo } = await getCurrentUser();
            setCurrentUser(user);
            setCurrentUserPhoto(photo);
        } catch (e) {
            console.warn(e);
        } finally {
            setUserLoading(false);
        }
    };

    useAsyncEffect(async () => {
        if (!currentUser) await getUserAction();
    }, [currentUser]);

    const isLoading = userLoading || securityLoading;

    return {
        currentUser,
        currentUserPhoto,
        userApps,
        userRoles,
        userPermissions,
        userPermissionKeys,
        isSimulating,
        isSimulatingRole,
        simulatedRoleLabel,
        realUserEmail,
        realUserFullName,
        isUnrestricted,
        allowedPropertyCount,
        dataScopes,
        mapStateProvinceFilter,
        canViewFinancials,
        securityLoading,
        userLoading,
        isLoading,
    };
};

export default useCurrentUser;
